/* =========================================================
   「最近見た商品」: sections/chk-recently-viewed.liquid

   【役割】
   ・商品ページで閲覧履歴（商品ID＋閲覧日時）を localStorage に記録する
   ・セクションが置かれたページで、履歴から商品カードを取得して描画する

   【Horizon標準との関係】
   Horizonにも標準の閲覧履歴（assets/recently-viewed-products.js、
   localStorageキー 'viewedProducts'、最大4件・期限なし・検索ドロワー用）がある。
   本実装は保存件数10件・保持期間10日という別仕様のため、キーを分けて併存させる。
   Horizon標準のスクリプト・キーには一切触れないこと（同じキーを使うと、
   商品ページを開くたびにHorizon側が4件へ切り詰めてしまい両方が壊れる）。

   【カードの取得方法】
   Horizonの検索エンドポイント × Section Rendering API を使う。
     /search?q=id:A OR id:B…&resources[type]=product&section_id=<セクションID>
   1リクエストで全件分のHTMLが返る。削除・非公開の商品は検索結果に含まれない
   ため、除外処理を書かずに済む。並び順は関連度順で返るため、描画時に
   localStorage の閲覧日時順へ並べ替える。

   ========================================================= */

(function () {
  if (!window.customElements || customElements.get('chk-recently-viewed')) return;

  /* ---------------------------------------------------------
     定数（★仕様変更はここ1箇所★）
     運用者が触る必要のない値のため、スキーマには出していない。
  --------------------------------------------------------- */
  const STORAGE_KEY = 'chk:recently-viewed'; // Horizon標準の 'viewedProducts' とは別キー
  const MAX_STORED = 10; // 保存件数（表示件数より多めに持ち、欠番の補填に使う）
  const RETENTION_DAYS = 10; // 保持期間（最後に見た日から起算）
  const RETENTION_MS = RETENTION_DAYS * 24 * 60 * 60 * 1000;

  /* ---------------------------------------------------------
     localStorage の読み書き
  --------------------------------------------------------- */

  function readRaw() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      // 壊れた値が入っていても表示を止めない
      return [];
    }
  }

  function save(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (error) {
      // プライベートブラウズ等で書き込めない場合は黙って諦める
    }
  }

  // 履歴を新しい順で返す。読み出しのたびに期限切れを除去して書き戻す。
  function getHistory() {
    const now = Date.now();
    const raw = readRaw().filter((entry) => entry && entry.id && typeof entry.viewedAt === 'number');
    const alive = raw
      .filter((entry) => now - entry.viewedAt < RETENTION_MS)
      .sort((a, b) => b.viewedAt - a.viewedAt)
      .slice(0, MAX_STORED);

    // 期限切れを落とした／件数を超えていた場合だけ書き戻す
    if (alive.length !== raw.length) save(alive);

    return alive;
  }

  /* ---------------------------------------------------------
     ★記録処理はこの関数1つに集約している★

     現時点では同意管理（同意バナー）を実装していない。保存するのは商品IDと
     閲覧日時のみで、個人を特定する情報を含まず第三者にも送信しない。
     将来、同意バナーを導入する場合は、この関数の先頭に
       if (!同意済み) return;
     のガードを1つ足すだけで記録を止められる。他の箇所に記録処理を
     書き足さないこと。
  --------------------------------------------------------- */
  function recordView(productId) {
    if (!productId) return;

    const id = String(productId);
    // 同じ商品を再訪したら日時を更新して先頭へ移動する（＝保持期間がリセットされる）
    const list = getHistory().filter((entry) => entry.id !== id);
    list.unshift({ id: id, viewedAt: Date.now() });
    save(list.slice(0, MAX_STORED));
  }

  // 取得できなかった商品（削除・非公開）を履歴から取り除く
  function forgetIds(ids) {
    if (!ids.length) return;
    save(getHistory().filter((entry) => ids.indexOf(entry.id) === -1));
  }

  /* ---------------------------------------------------------
     商品ページでの記録
     snippets/chk-head.liquid が商品ページにだけ出力する
     <meta name="chk-product-id"> から商品IDを受け取る。
  --------------------------------------------------------- */
  const productMeta = document.querySelector('meta[name="chk-product-id"]');
  if (productMeta) recordView(productMeta.content);

  /* ---------------------------------------------------------
     セクション本体
  --------------------------------------------------------- */

  customElements.define(
    'chk-recently-viewed',
    class ChkRecentlyViewed extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-chk-rv-list]');
        if (!this.list) return;

        this.sectionId = this.dataset.sectionId;
        this.limit = parseInt(this.dataset.limit, 10) || 4;
        this.excludeId = this.dataset.excludeId || '';

        // 背景色用の兄弟要素（設定されている場合のみ存在する）。一緒に出し入れする。
        const previous = this.previousElementSibling;
        this.background = previous && previous.hasAttribute('data-chk-rv-background') ? previous : null;

        // テーマエディタでの再描画に備えて中身を空にしてから組み直す
        this.list.innerHTML = '';

        this.build();
      }

      async build() {
        // 商品ページでは表示中の商品を除外する。
        // 結果から省くのではなくクエリを組み立てる段階で除外している。
        // 検索の対象件数が1件減るぶん無駄がなく、表示枠を1つも損なわないため。
        const history = getHistory().filter((entry) => entry.id !== this.excludeId);

        // 有効な履歴が0件ならリクエストを行わず、セクションごと非表示のままにする
        if (!history.length) return;

        // 履歴がある時点で表示を始める（高さを確保した状態）。
        // localStorage の読み出しは同期処理のため、ここでのちらつきは発生しない。
        this.reveal(true);

        let cards = null;
        try {
          cards = await this.fetchCards(history.map((entry) => entry.id));
        } catch (error) {
          // 取得に失敗しても画面にエラーを出さない（セクションを閉じるだけ）
          cards = null;
        }

        if (!cards || cards.size === 0) {
          this.reveal(false);
          return;
        }

        // 検索は関連度順で返るため、localStorage の閲覧日時順（新しい順）へ並べ替える。
        // history が既に新しい順なので、その順に取り出して詰めるだけでよい。
        const fragment = document.createDocumentFragment();
        const missing = [];
        let shown = 0;

        history.forEach((entry) => {
          const card = cards.get(entry.id);
          if (!card) {
            // 検索結果に無い＝削除・非公開。履歴からも消す
            missing.push(entry.id);
            return;
          }
          // 表示は先頭 limit 件まで。残りは欠番が出たときの補填用に保存してある分。
          if (shown >= this.limit) return;
          fragment.appendChild(card);
          shown += 1;
        });

        forgetIds(missing);

        if (shown === 0) {
          this.reveal(false);
          return;
        }

        this.list.appendChild(fragment);
        this.dataset.state = 'ready'; // 高さ確保を解除する
      }

      // 検索エンドポイント × Section Rendering API で商品カードのHTMLを取得する
      async fetchCards(ids) {
        if (!this.sectionId) return null;

        // ロケール別URL（/ja/search 等）に対応するため Theme.routes を優先して使う。
        // Theme は const 宣言のため window 経由では参照できない点に注意。
        const searchPath =
          typeof Theme !== 'undefined' && Theme.routes && Theme.routes.search_url ? Theme.routes.search_url : '/search';

        const url = new URL(searchPath, window.location.origin);
        url.searchParams.set('q', ids.map((id) => 'id:' + id).join(' OR '));
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('section_id', this.sectionId);

        const response = await fetch(url.toString());
        if (!response.ok) return null;

        const markup = await response.text();
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const source = parsed.querySelector('[data-chk-rv-cards]');
        if (!source) return null;

        // 目印は data-chk-rv-id（専用の属性名）。data-product-id を使うと、
        // Horizonの商品カード内部にある同名属性の要素まで拾ってしまい、
        // カード本体ではなく内部要素を取り込んでしまう。
        // さらに直下の子だけに限定して、入れ子の取り違えを二重に防ぐ。
        const cards = new Map();
        source.querySelectorAll(':scope > [data-chk-rv-id]').forEach((item) => {
          cards.set(item.dataset.chkRvId, document.importNode(item, true));
        });

        return cards;
      }

      // セクション（と背景要素）の表示・非表示を切り替える
      reveal(show) {
        this.hidden = !show;
        if (this.background) this.background.hidden = !show;
        if (show) this.dataset.state = 'loading';
      }
    }
  );
})();
